import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslations } from 'js/hooks/i18n';
import { clsb } from 'js/utils/classNames.util';

import * as Types from '../../types';
import { AutocompleteOption } from '../AutocompleteOption';

import style from './AutocompleteOptions.scss';

export const AutocompleteOptions: React.FC<Types.IAutocompleteOptionsProps> = (props) => {
    const {
        activeValue,
        chosenValue,
        classNames,
        fieldId,
        noOptionsMessage,
        onOptionClick,
        Option,
        options,
        optionsContainerRef,
        setActiveValue,
        visibleOptionsCount
    } = props;
    const translate = useTranslations();
    const optionsRef = useRef<HTMLUListElement>(null);
    const [scrollTop, setScrollTop] = useState<number>(0);
    const [itemHeight, setItemHeight] = useState<number>(0);
    const [firstItemIndex, setFirstItemIndex] = useState<number>(0);
    const [virtualOptions, setVirtualItems] = useState<Types.IAutocompleteOption[]>(options);

    const handleDocumentKeyDown = useCallback((event: KeyboardEvent): void => {
        if (!virtualOptions.length) {
            return;
        }

        switch (event.key) {
            case 'ArrowUp':
            case 'ArrowDown': {
                event.preventDefault();

                setActiveValue(currentValue => {
                    if (currentValue === null) {
                        return virtualOptions[0].value;
                    }

                    const nextIndex = event.key === 'ArrowDown' ? 1 : -1;
                    const nextOptionIndex = virtualOptions.findIndex(item => item.value === currentValue) + nextIndex;

                    return virtualOptions[nextOptionIndex]?.value || currentValue;
                });

                break;
            }
            default:
                setActiveValue(undefined);
                break;
        }
    }, [virtualOptions, setActiveValue]);

    useEffect(() => {
        document.addEventListener('keydown', handleDocumentKeyDown);

        return () => {
            document.removeEventListener('keydown', handleDocumentKeyDown);
        };
    }, [handleDocumentKeyDown]);

    const handleOnScroll = useCallback((event: React.SyntheticEvent<HTMLDivElement>): void => {
        setScrollTop(event.currentTarget.scrollTop);
    }, []);

    useEffect(() => {
        if (!scrollTop) {
            setVirtualItems(options.slice(0, 2 * visibleOptionsCount));
            setFirstItemIndex(0);

            return;
        }

        if (!options.length) {
            setScrollTop(0);

            return;
        }

        const itemIndex = Math.round(scrollTop / itemHeight);
        const nextFirstItemIndex = Math.max(0, itemIndex - visibleOptionsCount);

        setVirtualItems(options.slice(nextFirstItemIndex, itemIndex + 2 * visibleOptionsCount));
        setFirstItemIndex(nextFirstItemIndex);
    }, [itemHeight, scrollTop, options, visibleOptionsCount]);

    useEffect(() => {
        if (!itemHeight && virtualOptions.length && optionsRef.current) {
            const { height } = optionsRef.current.children[0].getBoundingClientRect();

            setItemHeight(height);
        }
    }, [itemHeight, virtualOptions.length]);

    return (
        <div
            ref={optionsContainerRef}
            onScroll={handleOnScroll}
            className={`${clsb([style, classNames], ['optionsContainer'])} cms-autocomplete-field-options`}
            style={{
                overflow: virtualOptions.length ? 'auto' : 'hidden',
                ...itemHeight && {
                    minHeight: `${itemHeight}px`,
                    height: `${Math.min(visibleOptionsCount, options.length) * itemHeight}px`
                }
            }}
        >
            <ul
                role="listbox"
                ref={optionsRef}
                className={clsb([style, classNames], ['options'])}
                style={{ ...itemHeight && {
                    height: `${options.length * itemHeight}px`,
                    maxHeight: `${options.length * itemHeight}px`
                } }}
            >
                {virtualOptions.length
                    ? virtualOptions.map((option, index) => (
                        <AutocompleteOption
                            Option={Option}
                            option={option}
                            fieldId={fieldId}
                            key={option.value}
                            optionIndex={index}
                            itemHeight={itemHeight}
                            chosenValue={chosenValue}
                            activeValue={activeValue}
                            onOptionClick={onOptionClick}
                            firstItemIndex={firstItemIndex}
                            optionsContainerRef={optionsContainerRef}
                            className={clsb([style, classNames], ['option'])}
                        />
                    ))
                    : (
                        <li
                            role="option"
                            aria-live="polite"
                            className={`${clsb([style, classNames], ['option', 'noOptions'])} cms-autocomplete-field-option`}
                        >
                            {noOptionsMessage || translate('playtech.mobile.system.autocomplete.noOptions')}
                        </li>
                    )
                }
            </ul>
        </div>
    );
};
